import { useTranslation } from 'react-i18next';
import Dragger from 'antd/es/upload/Dragger';
import Select from 'antd/es/select';
import Spin from 'antd/es/spin';
import Switch from 'antd/es/switch';
import Input from 'antd/es/input';
import DatePicker from 'react-datepicker';
import { DocumentIcon } from 'shared/ui/icons';
import {
  TEXTAREA_ROWS,
  YEAR_DROPDOWN_ITEM_COUNT,
  DATE_FORMAT,
  ACCEPTED_FILE_TYPES,
  SUPPORTED_FORMATS_LABEL,
  FILE_DOC_PRIMARY_COLOR,
} from '../modal-upload-tariff-book.constants';

export function OrganizationField({
  value,
  error,
  disabled,
  organizations,
  organizationLoading,
  hospitalSearch,
  onSearch,
  onChange,
  onClear,
  onPopupScroll,
}) {
  const { t } = useTranslation('common');
  const optionsList = hospitalSearch
    ? [{ label: hospitalSearch, value: hospitalSearch }, ...organizations]
    : organizations;

  return (
    <div className="w-full flex flex-col space-y-2">
      <p className="text-sm font-medium text-neutral-700">
        {t('label_hospital')}
      </p>
      <Select
        allowClear
        showSearch
        size="large"
        disabled={disabled}
        placeholder={t('label_hospital')}
        value={value || undefined}
        onChange={onChange}
        onSearch={onSearch}
        onClear={onClear}
        onPopupScroll={onPopupScroll}
        loading={organizationLoading}
        filterOption={false}
        options={optionsList}
        optionRender={(option) => (
          <div className="flex gap-4 py-1 text-base">{option.label}</div>
        )}
        style={{ width: '100%' }}
      />
      {error ? (
        <div className="w-full justify-start items-start flex">
          <span role="alert" className="text-error-500 text-sm">
            {t('hospital_name_required')}
          </span>
        </div>
      ) : null}
    </div>
  );
}

export function StartDateField({ value, error, disabled, onChange }) {
  const { t } = useTranslation('common');

  return (
    <div className="w-full flex flex-col space-y-2">
      <p className="text-sm font-medium text-neutral-700">{t('start_date')}</p>
      <DatePicker
        portalId="modal-root"
        selected={value || null}
        onChange={onChange}
        disabled={disabled}
        showYearDropdown
        scrollableYearDropdown
        yearDropdownItemNumber={YEAR_DROPDOWN_ITEM_COUNT}
        className="w-full rounded-md border px-4 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
        placeholderText={t('select_date')}
        dateFormat={DATE_FORMAT}
      />
      {error ? (
        <div className="w-full justify-start items-start flex">
          <span role="alert" className="text-error-500 text-sm">
            {error}
          </span>
        </div>
      ) : null}
    </div>
  );
}

export function CountryField({
  value,
  countries,
  countryLoading,
  disabled,
  onSearch,
  onChange,
  onPopupScroll,
}) {
  const { t } = useTranslation('common');

  return (
    <div className="w-full flex flex-col space-y-2">
      <p className="text-sm font-medium text-neutral-700">
        {t('country')} ({t('optional')})
      </p>
      <Select
        size="large"
        showSearch
        allowClear
        disabled={disabled}
        placeholder={t('country')}
        value={value}
        onSearch={onSearch}
        onChange={onChange}
        onPopupScroll={onPopupScroll}
        notFoundContent={countryLoading ? <Spin size="small" /> : null}
        options={countries.map((u) => ({
          label: u?.name,
          value: u?.id,
        }))}
        style={{ width: '100%' }}
        filterOption={false}
      />
    </div>
  );
}

export function DiscountSection({
  activate,
  value,
  disabled,
  onToggle,
  onChange,
}) {
  const { t } = useTranslation('common');

  return (
    <div className="w-full flex flex-col space-y-2">
      <div className="flex items-center gap-2">
        <Switch
          size="small"
          checked={activate}
          disabled={disabled}
          onChange={onToggle}
        />
        <p className="text-sm font-medium text-neutral-700">{t('discount')}</p>
      </div>
      <Input.TextArea
        rows={TEXTAREA_ROWS}
        disabled={disabled || !activate}
        value={value}
        onChange={onChange}
        placeholder={t('placeholder_discount_rule')}
      />
    </div>
  );
}

export function UploadFileField({
  file,
  loading,
  selectBulkFile,
  onDraggerChange,
}) {
  const { t } = useTranslation('common');

  return (
    <div className="w-full flex flex-col space-y-2">
      <label htmlFor="name" className="text-base font-normal">
        {t('upload_file')}
      </label>
      {file ? (
        <SelectedFilePreview
          file={file}
          loading={loading}
          selectBulkFile={selectBulkFile}
          onDraggerChange={onDraggerChange}
        />
      ) : (
        <FileDragger onDraggerChange={onDraggerChange} />
      )}
    </div>
  );
}

function SelectedFilePreview({
  file,
  loading,
  selectBulkFile,
  onDraggerChange,
}) {
  const { t } = useTranslation('common');
  return (
    <div className="bg-surface-primary-light border border-dashed flex flex-col gap-1 items-center justify-center text-left min-h-40 rounded-md">
      <DocumentIcon
        color={FILE_DOC_PRIMARY_COLOR}
        stroke={FILE_DOC_PRIMARY_COLOR}
        aria-hidden="true"
      />
      <div className="text-sm flex justify-center font-medium text-[#41337A] text-center px-2 my-1">
        {file.name}
      </div>
      <div className="text-xs text-[#5D42BD] mb-1">{file.size} kb</div>
      {loading ? null : (
        <div>
          <button
            type="button"
            className="flex w-auto justify-center rounded-md border border-surface-primary-light px-3 py-1 text-xs leading-6 text-black shadow-sm bg-white"
            onClick={() => selectBulkFile?.current.click()}
          >
            {t('edit')} File
          </button>
          <input
            type="file"
            id="getFileBulk"
            className="hidden"
            ref={selectBulkFile}
            onInput={() =>
              onDraggerChange(selectBulkFile.current?.files[0], true)
            }
            accept={ACCEPTED_FILE_TYPES}
          />
        </div>
      )}
    </div>
  );
}

function FileDragger({ onDraggerChange }) {
  const { t } = useTranslation('common');
  return (
    <Dragger
      className="bg-blue-50 border rounded-md border-dashed"
      multiple={false}
      onChange={(e) => onDraggerChange(e)}
      beforeUpload={() => false}
      accept={ACCEPTED_FILE_TYPES}
    >
      <div className="flex flex-col gap-1 items-center justify-center text-left min-h-32">
        <DocumentIcon
          color={FILE_DOC_PRIMARY_COLOR}
          stroke={FILE_DOC_PRIMARY_COLOR}
          aria-hidden="true"
        />
        <div className="text-md text-[#41337A]">
          {t('drag_and_drop_or_browse')}
        </div>
        <div className="text-xs text-blue-500">
          {t('supported_formats')}: {SUPPORTED_FORMATS_LABEL}
        </div>
      </div>
    </Dragger>
  );
}
